import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { LeaveTypeModal } from '../components/LeaveTypeModal';
import { leaveTypeApi } from '../leaveApi';
import {
  LEAVE_CREDIT_KIND_LABELS,
  leaveCreditShareLabel,
  type LeaveType,
} from '../leaveTypes';

/**
 * The leave-type catalog — what every employee's Credit Balance table is built from.
 *
 * The anchor opens the **profile**, not the edit form: every index page in this product is
 * partnered with one, and here the profile is where the count of entries already filed against
 * the type is worth reading before deciding anything about the row.
 */
export function LeaveTypesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<LeaveType | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    type: LeaveType;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchTypes = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      leaveTypeApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (type: LeaveType): MenuItem[] => [
      {
        key: `edit-${type.id}`,
        label: 'Edit',
        permission: 'leave-types.update',
        onClick: () => setEditing(type),
      },
      {
        key: `restore-${type.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'leave-types.delete',
        disabledReason: type.isActive
          ? 'This leave type is already active.'
          : type.parentLeaveTypeId != null && !type.parentIsActive
            ? `Its parent type, ${type.parentLeaveTypeName ?? 'now deleted'}, is deleted. Restore the parent first.`
            : null,
        onClick: () => setConfirming({ kind: 'activate', type }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await leaveTypeApi.deactivate(confirming.type.id);
      else await leaveTypeApi.activate(confirming.type.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<LeaveType>
        module="leave-types"
        // Reached through Personnel → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Personnel',
          { label: 'Settings', to: '/personnel/settings' },
          'Leave Types',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['LeaveType']}
        rowKey={(type) => type.id}
        fetch={fetchTypes}
        reloadKey={reloadKey}
        searchPlaceholder="Search leave types"
        emptyMessage="No leave types yet. Add the kinds of leave this organization grants."
        onCreate={() => setCreating(true)}
        createPermission="leave-types.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(type) => navigate(`/personnel/settings/leave-types/${type.id}`)}
        openPermission="leave-types.read"
        rowActions={rowActions}
        onDelete={(type) => setConfirming({ kind: 'deactivate', type })}
        deletePermission="leave-types.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(type) =>
          !type.isActive
            ? 'This leave type is already deleted.'
            : type.childCount > 0
              ? 'This type has child types. Delete them, or move them under another type, first.'
              : null}
        onClearData={() => leaveTypeApi.clear()}
        clearDataPermission="leave-types.clear-data"
        otherInfo={(type) => type}
      />

      {creating || editing !== null ? (
        <LeaveTypeModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete leave type' : 'Restore leave type'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`leave-type-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.type.name}" stops being listed on every employee's Credit Balance.`
                  : `"${confirming.type.name}" is listed again on every employee's Credit Balance.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.type.recordCount > 0 ? (
                <p>
                  {confirming.type.recordCount} ledger entr
                  {confirming.type.recordCount === 1 ? 'y has' : 'ies have'} already been filed
                  against it. Those entries stay in the ledger and still name the type — removing
                  it from the catalog does not erase what employees have earned.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted leave type can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<LeaveType>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 240,
    flex: true,
    sortable: true,
    render: (type) => <TextCell value={type.name} />,
  },
  {
    key: 'parentLeaveTypeId',
    header: 'Parent type',
    width: 180,
    // Not sortable: the server orders by the catalog, not by the parent's name.
    render: (type) => <TextCell value={type.parentLeaveTypeName} />,
  },
  {
    key: 'sharesParentCredit',
    header: 'Credit',
    width: 180,
    render: (type) => <TextCell value={leaveCreditShareLabel(type)} />,
  },
  {
    key: 'code',
    header: 'Code',
    width: 100,
    sortable: true,
    summary: true,
    render: (type) => <TextCell value={type.code} mono />,
  },
  {
    key: 'creditKind',
    header: 'Tracked as',
    width: 140,
    summary: true,
    render: (type) => <TextCell value={LEAVE_CREDIT_KIND_LABELS[type.creditKind]} />,
  },
  {
    key: 'monthlyAccrualDays',
    header: 'Monthly accrual',
    width: 140,
    align: 'right',
    /*
      An em dash, not a muted zero. A type with no rate does not accrue by the calendar at all
      — Compensatory Leave is earned from rendered overtime — which is a different fact from
      accruing none each month, and the two must not read alike.
    */
    render: (type) => (
      <TextCell
        value={type.monthlyAccrualDays == null ? null : type.monthlyAccrualDays.toFixed(2)}
        mono
      />
    ),
  },
  {
    key: 'description',
    header: 'Description',
    width: 300,
    summary: true,
    render: (type) => <TextCell value={type.description} />,
  },
  {
    // The stored sort value, under a heading that says so: a POSITION is only correct with the
    // whole ladder in hand, and a paged table never has it.
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (type) => <TextCell value={String(type.sortOrder)} mono numeric />,
  },
  {
    key: 'recordCount',
    header: 'Entries',
    width: 100,
    align: 'right',
    summary: true,
    // The count is of ledger entries, which are another module's rows — the name and kind
    // beside it are this module's own and stay ungated.
    permission: 'leave-credits.read',
    render: (type) => <TextCell value={String(type.recordCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (type) =>
      type.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
